import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Space,
  Switch,
  Typography,
  message,
} from 'antd';
import {
  ApiOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import {
  getMeituanInventoryConfig,
  saveMeituanInventoryConfig,
  type MeituanInventoryTimeSlot,
} from '../services/meituanInventoryConfigService';

interface MeituanInventoryConfigFormValues {
  enabled: boolean;
  defaultDays: number;
  timeSlots: MeituanInventoryTimeSlot[];
  remark?: string;
}

const TIME_PATTERN = /^([01]\d|2[0-3]):00$/;

const createDefaultSlot = (): MeituanInventoryTimeSlot => ({
  begin: '',
  end: '',
  remainCount: 999,
});

const parseMinutes = (value: string) => {
  if (!TIME_PATTERN.test(value)) {
    return null;
  }
  const [hour, minute] = value.split(':').map(Number);
  return hour * 60 + minute;
};

const sortSlots = (slots: MeituanInventoryTimeSlot[]) => {
  return [...slots].sort((left, right) => {
    const leftBegin = parseMinutes(left.begin) ?? Number.MAX_SAFE_INTEGER;
    const rightBegin = parseMinutes(right.begin) ?? Number.MAX_SAFE_INTEGER;
    if (leftBegin === rightBegin) {
      return (parseMinutes(left.end) ?? Number.MAX_SAFE_INTEGER) - (parseMinutes(right.end) ?? Number.MAX_SAFE_INTEGER);
    }
    return leftBegin - rightBegin;
  });
};

const validateTimeSlots = (_: unknown, value?: MeituanInventoryTimeSlot[]) => {
  const slots = (value || []).map((item) => ({
    begin: item?.begin?.trim() || '',
    end: item?.end?.trim() || '',
    remainCount: Number(item?.remainCount ?? 0),
  }));

  if (!slots.length) {
    return Promise.reject(new Error('请至少配置一个库存时间段'));
  }

  const normalized = sortSlots(slots);
  let previousEndMinutes: number | null = null;

  for (let index = 0; index < normalized.length; index += 1) {
    const slot = normalized[index];
    if (!slot.begin || !slot.end) {
      return Promise.reject(new Error(`第 ${index + 1} 个库存时间段不能为空`));
    }

    const beginMinutes = parseMinutes(slot.begin);
    if (beginMinutes === null) {
      return Promise.reject(new Error(`第 ${index + 1} 个开始时间必须是整点，请使用 HH:00`));
    }

    const endMinutes = parseMinutes(slot.end);
    if (endMinutes === null) {
      return Promise.reject(new Error(`第 ${index + 1} 个结束时间必须是整点，请使用 HH:00`));
    }

    if (beginMinutes >= endMinutes) {
      return Promise.reject(new Error(`第 ${index + 1} 个库存时间段开始时间必须早于结束时间`));
    }

    if (slot.remainCount < 0 || slot.remainCount > 999) {
      return Promise.reject(new Error(`第 ${index + 1} 个库存剩余量需在 0 到 999 之间`));
    }

    if (previousEndMinutes !== null && beginMinutes < previousEndMinutes) {
      return Promise.reject(new Error('库存时间段不能重叠'));
    }

    previousEndMinutes = endMinutes;
  }

  return Promise.resolve();
};

const MeituanInventoryConfigPage: React.FC = () => {
  const [form] = Form.useForm<MeituanInventoryConfigFormValues>();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const watchedEnabled = Form.useWatch('enabled', form);
  const watchedDefaultDays = Form.useWatch('defaultDays', form);
  const watchedSlots = Form.useWatch('timeSlots', form);

  const previewSlots = useMemo(() => {
    return sortSlots(
      (watchedSlots || [])
        .map((item) => ({
          begin: item?.begin?.trim() || '',
          end: item?.end?.trim() || '',
          remainCount: Number(item?.remainCount ?? 0),
        }))
        .filter((item) => item.begin && item.end),
    );
  }, [watchedSlots]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMeituanInventoryConfig();
      form.setFieldsValue({
        enabled: res.data?.enabled ?? true,
        defaultDays: res.data?.defaultDays || 7,
        timeSlots: res.data?.timeSlots?.length ? res.data.timeSlots : [createDefaultSlot()],
        remark: res.data?.remark || '',
      });
    } catch {
      message.error('加载美团库存配置失败');
      form.setFieldsValue({
        enabled: true,
        defaultDays: 7,
        timeSlots: [createDefaultSlot()],
        remark: '',
      });
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);
      const payload = {
        enabled: values.enabled,
        defaultDays: values.defaultDays,
        timeSlots: sortSlots(
          (values.timeSlots || []).map((item) => ({
            begin: item.begin.trim(),
            end: item.end.trim(),
            remainCount: Number(item.remainCount),
          })),
        ),
        remark: values.remark?.trim() || '',
      };
      await saveMeituanInventoryConfig(payload);
      message.success('保存成功');
      form.setFieldsValue(payload);
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.response?.data?.message || '保存美团库存配置失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        loading={loading}
        title={(
          <Space size={8}>
            <ApiOutlined />
            <span>美团库存配置</span>
          </Space>
        )}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void loadData()}>
              刷新
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={submitLoading} onClick={() => void handleSubmit()}>
              保存配置
            </Button>
          </Space>
        )}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          message="用于美团到家行业库存查询"
          description="美团 msgType=5810009 回调会按这里配置的日期范围和整点时间段返回库存。关闭库存时，仍返回时间段但剩余库存为 0。"
        />

        <Form<MeituanInventoryConfigFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            enabled: true,
            defaultDays: 7,
            timeSlots: [createDefaultSlot()],
            remark: '',
          }}
        >
          <Form.Item label="启用库存" name="enabled" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="关闭" />
          </Form.Item>

          <Form.Item
            label="最大返回库存天数"
            name="defaultDays"
            rules={[
              { required: true, message: '请输入最大返回库存天数' },
              { type: 'number', min: 1, max: 365, message: '最大返回库存天数需在 1 到 365 之间' },
            ]}
            extra="美团请求 days 超过该值时，将按该值截断返回。"
          >
            <InputNumber min={1} max={365} precision={0} style={{ width: 220 }} placeholder="请输入天数" />
          </Form.Item>

          <Form.Item label="每日库存时间段" required>
            <Form.Item name="timeSlots" noStyle rules={[{ validator: validateTimeSlots }]}>
              <div />
            </Form.Item>
            <Form.List name="timeSlots">
              {(fields, { add, remove }) => (
                <Space orientation="vertical" size={16} style={{ width: '100%' }}>
                  {fields.map((field, index) => (
                    <Space key={field.key} align="start" wrap>
                      <Form.Item
                        {...field}
                        label={index === 0 ? '开始时间' : ' '}
                        name={[field.name, 'begin']}
                        style={{ marginBottom: 0 }}
                        rules={[
                          { required: true, message: '请输入开始时间' },
                          { pattern: TIME_PATTERN, message: '请使用 HH:00 整点格式' },
                        ]}
                      >
                        <Input placeholder="例如 09:00" style={{ width: 150 }} maxLength={5} />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        label={index === 0 ? '结束时间' : ' '}
                        name={[field.name, 'end']}
                        style={{ marginBottom: 0 }}
                        rules={[
                          { required: true, message: '请输入结束时间' },
                          { pattern: TIME_PATTERN, message: '请使用 HH:00 整点格式' },
                        ]}
                      >
                        <Input placeholder="例如 10:00" style={{ width: 150 }} maxLength={5} />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        label={index === 0 ? '剩余库存' : ' '}
                        name={[field.name, 'remainCount']}
                        style={{ marginBottom: 0 }}
                        rules={[
                          { required: true, message: '请输入剩余库存' },
                          { type: 'number', min: 0, max: 999, message: '剩余库存需在 0 到 999 之间' },
                        ]}
                      >
                        <InputNumber min={0} max={999} precision={0} style={{ width: 140 }} />
                      </Form.Item>
                      <Form.Item label={index === 0 ? '操作' : ' '} style={{ marginBottom: 0 }}>
                        <Button
                          danger
                          icon={<MinusCircleOutlined />}
                          disabled={fields.length <= 1}
                          onClick={() => remove(field.name)}
                        >
                          删除
                        </Button>
                      </Form.Item>
                    </Space>
                  ))}
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add(createDefaultSlot())}>
                    新增库存时间段
                  </Button>
                </Space>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="可填写配置说明，仅后台展示" />
          </Form.Item>
        </Form>

        <Card size="small" title="返回预览" style={{ marginTop: 24, background: '#fafafa' }}>
          <Space orientation="vertical" size={8}>
            <Typography.Text>
              库存状态：{watchedEnabled === false ? '关闭，返回库存为 0' : '启用'}
            </Typography.Text>
            <Typography.Text>
              最大返回库存天数：{watchedDefaultDays || 7} 天
            </Typography.Text>
            <Typography.Text>
              时间段：
              {previewSlots.length
                ? previewSlots.map((item) => `${item.begin}-${item.end}：${watchedEnabled === false ? 0 : item.remainCount}`).join('、')
                : '暂未配置'}
            </Typography.Text>
          </Space>
        </Card>
      </Card>
    </div>
  );
};

export default MeituanInventoryConfigPage;
